import { memo } from 'react'
import { Radio } from 'lucide-react'
import type { ScheduleItem } from '../../types/index.js'
import { formatTime, scheduleItemTitle } from '../../lib/utils.js'

/**
 * Bandeau d'impasse : le direct du créneau n'est pas à l'antenne.
 *
 * Il prend la place de `LiveControlBanner`, dont les gestes (« Fin du direct »,
 * « Décrocher ») visent un direct à l'antenne et étaient tous refusés. Le
 * 01/10/2026, la suite d'un direct est passée en ERREUR (boucle d'attente
 * introuvable) : l'image est restée figée sur la fin du jingle et aucun bouton
 * ne permettait de mettre le studio à l'antenne, jusqu'au redémarrage du
 * playout.
 *
 * « Lancer la source maintenant » met la source à l'antenne sans boucle
 * d'attente, jusqu'à la fin de grille du direct (`lib/liveStartNow.ts`).
 */

interface LiveStartNowBannerProps {
  item:     ScheduleItem
  disabled: boolean
  pending:  boolean
  onStart:  (item: ScheduleItem) => void
}

export const LiveStartNowBanner = memo(function LiveStartNowBanner({ item, disabled, pending, onStart }: LiveStartNowBannerProps) {
  const title = scheduleItemTitle(item, 'Direct')
  const failed = item.status === 'ERROR'

  return (
    <div className="monitor-manual-live" data-breaking="true" role="alert">
      <div className="monitor-manual-live__copy">
        <strong>{failed ? `Direct en erreur — ${title}` : `Direct non lancé — ${title}`}</strong>
        <span>
          {failed
            ? 'Le départ a échoué : la source n’est pas à l’antenne.'
            : `Prévu à ${formatTime(item.startTime)}, il n’est pas parti.`}
          {` Fin de grille à ${formatTime(item.endTime)}.`}
        </span>
      </div>

      <button
        type="button"
        className="tp-button tp-button--critical monitor-manual-live__button"
        disabled={disabled || pending}
        onClick={() => onStart(item)}
        title="Met la source de ce direct à l'antenne tout de suite, sans boucle d'attente, jusqu'à sa fin de grille"
      >
        <Radio size={16} aria-hidden="true" />
        {pending ? 'Lancement…' : 'Lancer la source maintenant'}
      </button>
    </div>
  )
})
